import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { TOUR_DEFINITIONS, TOUR_ORDER, getTourNameFromPath, type TourName } from "@/components/onboarding/tours";

type TourRecord = {
  id: string;
  user_id: string;
  tour_name: string;
  step_reached: number;
  completed_at: string | null;
  dismissed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type TourStatus = "completed" | "dismissed" | "in_progress" | "unseen";

export interface ActiveTourStep {
  id: string;
  title: string;
  description: string;
  selector: string;
}

interface OnboardingTourContextValue {
  activeTourName: TourName | null;
  currentTourName: TourName | null;
  currentStep: ActiveTourStep | null;
  currentStepIndex: number;
  totalSteps: number;
  isActive: boolean;
  allTourNames: TourName[];
  startTour: (tourName: TourName) => void;
  nextStep: () => void;
  prevStep: () => void;
  closeTour: () => void;
  resetTour: (tourName: TourName) => void;
  getTourStatus: (tourName: TourName) => TourStatus;
}

const OnboardingTourContext = createContext<OnboardingTourContextValue | null>(null);

function resolveTourSteps(tourName: TourName): ActiveTourStep[] {
  if (typeof document === "undefined") {
    return [];
  }

  return TOUR_DEFINITIONS[tourName].steps.flatMap((step) => {
    const selector = step.selectors.find((candidate) => document.querySelector(candidate));

    if (!selector) {
      return step.optional ? [] : [];
    }

    return [{
      id: step.id,
      title: step.title,
      description: step.description,
      selector,
    }];
  });
}

export function OnboardingTourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [activeTourName, setActiveTourName] = useState<TourName | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [activeSteps, setActiveSteps] = useState<ActiveTourStep[]>([]);
  const autoStartedToursRef = useRef<Set<TourName>>(new Set());

  const currentTourName = useMemo(() => getTourNameFromPath(location.pathname), [location.pathname]);

  const { data: tourRecords = [], isLoading } = useQuery({
    queryKey: ["user-onboarding-tours", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return [];
      }

      const { data, error } = await supabase
        .from("user_onboarding_tours")
        .select("*")
        .eq("user_id", user.id);

      if (error) {
        throw error;
      }

      return (data ?? []) as TourRecord[];
    },
    enabled: !!user?.id,
  });

  const persistTourState = useCallback(async (
    tourName: TourName,
    payload: Partial<Pick<TourRecord, "step_reached" | "completed_at" | "dismissed_at">>,
  ) => {
    if (!user?.id) {
      return;
    }

    const { error } = await supabase
      .from("user_onboarding_tours")
      .upsert({
        user_id: user.id,
        tour_name: tourName,
        step_reached: payload.step_reached ?? 0,
        completed_at: payload.completed_at ?? null,
        dismissed_at: payload.dismissed_at ?? null,
      }, {
        onConflict: "user_id,tour_name",
      });

    if (error) {
      throw error;
    }

    await queryClient.invalidateQueries({ queryKey: ["user-onboarding-tours", user.id] });
  }, [queryClient, user?.id]);

  const getTourStatus = useCallback((tourName: TourName): TourStatus => {
    const record = tourRecords.find((item) => item.tour_name === tourName);

    if (!record) {
      return "unseen";
    }

    if (record.completed_at) {
      return "completed";
    }

    if (record.dismissed_at) {
      return "dismissed";
    }

    if (record.step_reached > 0) {
      return "in_progress";
    }

    return "unseen";
  }, [tourRecords]);

  const startTour = useCallback((tourName: TourName) => {
    const steps = resolveTourSteps(tourName);

    if (steps.length === 0) {
      return;
    }

    void persistTourState(tourName, {
      step_reached: 1,
      completed_at: null,
      dismissed_at: null,
    });

    setActiveTourName(tourName);
    setActiveSteps(steps);
    setCurrentStepIndex(0);
  }, [persistTourState]);

  const closeTour = useCallback(() => {
    if (!activeTourName) {
      return;
    }

    void persistTourState(activeTourName, {
      step_reached: currentStepIndex + 1,
      dismissed_at: new Date().toISOString(),
      completed_at: null,
    });

    setActiveTourName(null);
    setActiveSteps([]);
    setCurrentStepIndex(0);
  }, [activeTourName, currentStepIndex, persistTourState]);

  const nextStep = useCallback(() => {
    if (!activeTourName || activeSteps.length === 0) {
      return;
    }

    const nextIndex = currentStepIndex + 1;

    if (nextIndex >= activeSteps.length) {
      void persistTourState(activeTourName, {
        step_reached: activeSteps.length,
        completed_at: new Date().toISOString(),
        dismissed_at: null,
      });

      setActiveTourName(null);
      setActiveSteps([]);
      setCurrentStepIndex(0);
      return;
    }

    setCurrentStepIndex(nextIndex);
    void persistTourState(activeTourName, {
      step_reached: nextIndex + 1,
      completed_at: null,
      dismissed_at: null,
    });
  }, [activeSteps.length, activeTourName, currentStepIndex, persistTourState]);

  const prevStep = useCallback(() => {
    if (!activeTourName || currentStepIndex === 0) {
      return;
    }

    const prevIndex = currentStepIndex - 1;
    setCurrentStepIndex(prevIndex);
    void persistTourState(activeTourName, {
      step_reached: prevIndex + 1,
      completed_at: null,
      dismissed_at: null,
    });
  }, [activeTourName, currentStepIndex, persistTourState]);

  const resetTour = useCallback((tourName: TourName) => {
    void persistTourState(tourName, {
      step_reached: 0,
      completed_at: null,
      dismissed_at: null,
    });

    autoStartedToursRef.current.delete(tourName);
  }, [persistTourState]);

  useEffect(() => {
    if (!currentTourName || isLoading || activeTourName || !user?.id) {
      return;
    }

    if (autoStartedToursRef.current.has(currentTourName)) {
      return;
    }

    const status = getTourStatus(currentTourName);

    if (status === "completed" || status === "dismissed") {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      startTour(currentTourName);
      autoStartedToursRef.current.add(currentTourName);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeTourName, currentTourName, getTourStatus, isLoading, startTour, user?.id]);

  useEffect(() => {
    if (!activeTourName) {
      return;
    }

    if (!currentTourName || currentTourName !== activeTourName) {
      setActiveTourName(null);
      setActiveSteps([]);
      setCurrentStepIndex(0);
    }
  }, [activeTourName, currentTourName]);

  useEffect(() => {
    const currentStep = activeSteps[currentStepIndex];

    if (!currentStep) {
      return;
    }

    const element = document.querySelector<HTMLElement>(currentStep.selector);

    if (!element) {
      return;
    }

    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center",
    });
  }, [activeSteps, currentStepIndex]);

  const value = useMemo<OnboardingTourContextValue>(() => ({
    activeTourName,
    currentTourName,
    currentStep: activeSteps[currentStepIndex] ?? null,
    currentStepIndex,
    totalSteps: activeSteps.length,
    isActive: !!activeTourName && activeSteps.length > 0,
    allTourNames: TOUR_ORDER,
    startTour,
    nextStep,
    prevStep,
    closeTour,
    resetTour,
    getTourStatus,
  }), [
    activeSteps,
    activeTourName,
    closeTour,
    currentStepIndex,
    currentTourName,
    getTourStatus,
    nextStep,
    prevStep,
    resetTour,
    startTour,
  ]);

  return (
    <OnboardingTourContext.Provider value={value}>
      {children}
    </OnboardingTourContext.Provider>
  );
}

export function useOnboardingTour() {
  const context = useContext(OnboardingTourContext);

  if (!context) {
    throw new Error("useOnboardingTour must be used within OnboardingTourProvider");
  }

  return context;
}
